import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { LOCALE_ID, Provider } from '@angular/core';
import { loadTranslations } from '@angular/localize';

export type AppLanguage = 'it' | 'en';

export const LANGUAGE_STORAGE_KEY = 'language';

const EN_TRANSLATIONS_URL = '/shared-assets/i18n/en.json';

let currentLanguage: AppLanguage = 'it';

/** Effective language of this session, settled once `initI18n()` resolves. */
export function getCurrentLanguage(): AppLanguage {
  return currentLanguage;
}

export function resolveLanguage(): AppLanguage {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // localStorage can be unavailable (e.g. blocked cookies): ignore.
  }
  if (stored === 'it' || stored === 'en') {
    return stored;
  }
  return navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'it';
}

export function setLanguage(language: AppLanguage): void {
  if (language === currentLanguage) {
    return;
  }
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    return;
  }
  location.reload();
}

/**
 * Resolves the language and, for English, loads the runtime translations.
 * `$localize` messages are evaluated at module scope, so this must complete
 * BEFORE the application modules are imported: `main.ts` awaits it and then
 * dynamically imports `bootstrap.ts`.
 */
export async function initI18n(): Promise<void> {
  let language = resolveLanguage();

  if (language === 'en') {
    try {
      const response = await fetch(EN_TRANSLATIONS_URL);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const file = (await response.json()) as {
        translations: Record<string, string>;
      };
      loadTranslations(file.translations);
      $localize.locale = 'en';
    } catch (error) {
      console.warn(
        '[i18n] Could not load the English translations, falling back to Italian.',
        error,
      );
      language = 'it';
    }
  }

  currentLanguage = language;
  document.documentElement.lang = language;
}

export function provideAppI18n(): Provider[] {
  registerLocaleData(localeIt);
  return [
    {
      provide: LOCALE_ID,
      useValue: currentLanguage === 'it' ? 'it' : 'en-US',
    },
  ];
}
